// ============================================================================
// BOQ — Trade → canonical chapter crosswalk (render-time, non-destructive)
// ============================================================================
// Bills carry trade information in two different vocabularies depending on how
// they were built:
//
//   • SD estimate bills store the canonical brio_trade CHAPTER name directly in
//     project_items.trade (e.g. "Civil & Structural Works").
//   • Tender / measured bills are built from library items, whose lines store
//     the fine-grained library trade (the 2-letter item-code prefix) in
//     project_items.trade (e.g. "FL", "CC") and are classified in the 31-entry
//     `trade_group` taxonomy (EARTH, STRUCT, FLOOR, MEP-PLB …).
//
// That mismatch is why tender bills used to head with "PL", "CC" etc. instead of
// the 11 chapters. This crosswalk maps each fine `trade_group` onto the chapter
// it belongs to, so both kinds of bill roll up under the same 11 chapters — the
// fine trade is kept and surfaced as a sub-trade row. Nothing is written to the
// database; this is purely a presentation-layer rollup, so it's fully
// reversible and updates everywhere it's used (Bill grid + Summary).
//
// MISC / BASIC are deliberately left unmapped so they surface as their own
// section rather than being forced into a chapter.
//
// SOURCE OF TRUTH: as of the trade_group_chapter_crosswalk migration the link
// is stored in the database (boq.classification_nodes.maps_to_node_id), and the
// app reads it from there. This map is now only a defensive FALLBACK used when a
// trade_group node has no stored link — and it seeds the migration. To change a
// rollup permanently, update the DB link (and keep this in sync); the
// boq.trade_group_chapter_gaps view flags any in-use trade with no link.
// ============================================================================

// Keyed by boq.classification_nodes.code within the `trade_group` taxonomy.
// Values are the exact brio_trade chapter labels (taxonomy top-level labels).
export const CHAPTER_BY_TRADE_GROUP = {
  // 1 — Site Establishment (preliminaries / enabling)
  PREP: 'Site Establishment',
  PEST: 'Site Establishment',
  DEMO: 'Site Establishment',
  // 2 — Civil & Structural Works
  EARTH: 'Civil & Structural Works',
  STRUCT: 'Civil & Structural Works',
  MASON: 'Civil & Structural Works',
  PLAST: 'Civil & Structural Works',
  // 3 — Waterproofing
  WPRF: 'Waterproofing',
  // 4 — Roofing & Timber Structure
  ROOF: 'Roofing & Timber Structure',
  TIMBR: 'Roofing & Timber Structure',
  // 5 — Exterior Finishing
  STONE: 'Exterior Finishing',
  // 6 — Interior Finishing Works
  FLOOR: 'Interior Finishing Works',
  CEIL: 'Interior Finishing Works',
  PAINT: 'Interior Finishing Works',
  CNTR: 'Interior Finishing Works',
  DRYWL: 'Interior Finishing Works',
  PANEL: 'Interior Finishing Works',
  // 7 — Doors, Windows & Glazing
  DOOR: 'Doors, Windows & Glazing',
  GLAZE: 'Doors, Windows & Glazing',
  // 8 — Furniture & Joinery
  CARP: 'Furniture & Joinery',
  KITCH: 'Furniture & Joinery',
  // 9 — MEP & Services
  'MEP-PLB': 'MEP & Services',
  'MEP-ELE': 'MEP & Services',
  'MEP-HVAC': 'MEP & Services',
  'MEP-FIRE': 'MEP & Services',
  'MEP-SEC': 'MEP & Services',
  // 10 — Site Development
  SITE: 'Site Development',
  LAND: 'Site Development',
  POOL: 'Site Development',
  // 11 — Loose Interiors / FF&E
  FURN: 'Loose Interiors / FF&E',
  // Intentionally unmapped: MISC, BASIC (surface as their own section).
};

export default CHAPTER_BY_TRADE_GROUP;
